import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { isTestAccountEmail } from '../utils/testAccounts';

export type UserEntity = {
  id: string;
  name: string;
  type: 'mastermind' | 'community' | 'networkclub';
  role: 'owner' | 'organizer' | 'member';
  logoUrl?: string;
};

/**
 * Hook to get ALL entities where user is a member (any role).
 * Used for navbar "Groups" tab visibility and navigation.
 * 
 * Replaces useOwnedEntities — now includes regular members too,
 * so the Groups tab appears for anyone who is part of a group.
 */
export function useUserEntities(userId?: string | null, userEmail?: string | null) {
  const [entities, setEntities] = useState<UserEntity[]>([]);
  const [loading, setLoading] = useState(false);

  const isTestAccount = isTestAccountEmail(userEmail);

  useEffect(() => {
    if (!userId) {
      setEntities([]);
      return;
    }

    setLoading(true);
    let cancelled = false;
    let entityUnsubs: Array<() => void> = [];

    // Query ALL roles — not just owner/organizer
    const membersQuery = query(
      collection(db, 'entityMembers'),
      where('userId', '==', userId)
    );

    const clearEntityListeners = () => {
      entityUnsubs.forEach((unsub) => {
        try { unsub(); } catch {}
      });
      entityUnsubs = [];
    };

    const unsubscribe = onSnapshot(
      membersQuery,
      (membersSnap) => {
        try {
          clearEntityListeners();
          if (cancelled) return;

          const roleByEntityId = new Map<string, 'owner' | 'organizer' | 'member'>();
          membersSnap.docs.forEach((memberDoc) => {
            const member = memberDoc.data() as any;
            if (!member?.entityId) return;
            roleByEntityId.set(
              member.entityId,
              (member.role || 'member') as 'owner' | 'organizer' | 'member'
            );
          });

          const entityMap = new Map<string, UserEntity>();
          const publish = () => {
            if (cancelled) return;
            const next = Array.from(entityMap.values()).sort((a, b) => a.name.localeCompare(b.name));
            setEntities(next);
            setLoading(false);
          };

          if (roleByEntityId.size === 0) {
            setEntities([]);
            setLoading(false);
            return;
          }

          roleByEntityId.forEach((role, entityId) => {
            const unsubEntity = onSnapshot(
              doc(db, 'entities', entityId),
              (entitySnap) => {
                if (!entitySnap.exists()) {
                  entityMap.delete(entityId);
                  publish();
                  return;
                }

                const entity = entitySnap.data() as { name: string; type: string; logoUrl?: string; isActive?: boolean };
                if (!['mastermind', 'community', 'networkclub'].includes(entity.type) || entity.isActive === false) {
                  entityMap.delete(entityId);
                  publish();
                  return;
                }

                entityMap.set(entityId, {
                  id: entityId,
                  name: entity.name,
                  type: entity.type as 'mastermind' | 'community' | 'networkclub',
                  role,
                  logoUrl: entity.logoUrl,
                });
                publish();
              },
              (err) => {
                console.error('Error loading entity document:', err);
                entityMap.delete(entityId);
                publish();
              }
            );
            entityUnsubs.push(unsubEntity);
          });
        } catch (err) {
          if (cancelled) return;
          console.error('Error loading user entities:', err);
          setEntities([]);
          setLoading(false);
        }
      },
      (err) => {
        if (cancelled) return;
        console.error('Error loading user entities:', err);
        setEntities([]);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
      clearEntityListeners();
      unsubscribe();
    };
  }, [userId]);

  return {
    entities,
    loading,
    isTestAccount,
    /** True when Groups tab should be visible in navbar */
    showGroupsTab: entities.length > 0 || isTestAccount,
    /** True when user can manage (create meetings, invite, etc.) */
    canManage: (entityId: string) => {
      const entity = entities.find(e => e.id === entityId);
      return entity?.role === 'owner' || entity?.role === 'organizer';
    },
  };
}
